import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Navigation, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { cn } from '../lib/utils';

const GeoLocationCapture = ({ onLocationCapture, required = true, className, initialLocation = null }) => {
  const [location, setLocation] = useState(initialLocation);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('prompt');

  // Update location if initialLocation changes (for edit mode)
  useEffect(() => {
    if (initialLocation && !location) {
      setLocation(initialLocation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialLocation]);

  const checkPermission = useCallback(async () => {
    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        setPermissionStatus(result.state);
        result.onchange = () => setPermissionStatus(result.state);
      } catch {
        // Permission API not fully supported
      }
    }
  }, []);

  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  const captureLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setLoading(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString()
        };
        setLocation(loc);
        setLoading(false);
        if (onLocationCapture) {
          onLocationCapture(loc);
        }
      },
      (err) => {
        setLoading(false);
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setError('Location permission denied. Please enable location access in your browser settings.');
            break;
          case err.POSITION_UNAVAILABLE:
            setError('Location information is unavailable. Please try again.');
            break;
          case err.TIMEOUT:
            setError('Location request timed out. Please try again.');
            break;
          default:
            setError('An unknown error occurred while getting location.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  }, [onLocationCapture]);

  const formatCoordinate = (coord, type) => {
    const direction = type === 'lat' 
      ? (coord >= 0 ? 'N' : 'S')
      : (coord >= 0 ? 'E' : 'W');
    return `${Math.abs(coord).toFixed(6)}° ${direction}`;
  };

  return (
    <div className={cn("bg-white rounded-2xl border border-slate-200 p-5", className)}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={cn(
            "w-10 h-10 rounded-xl flex items-center justify-center",
            location ? "bg-emerald-100" : "bg-slate-100"
          )}>
            <MapPin className={cn("w-5 h-5", location ? "text-emerald-600" : "text-slate-500")} />
          </div>
          <div>
            <h3 className="font-medium text-slate-900">GPS Location</h3>
            <p className="text-xs text-slate-500">
              {required ? 'Required' : 'Optional'} • {permissionStatus === 'granted' ? 'Enabled' : 'Tap to capture'}
            </p>
          </div>
        </div>
        
        {location && (
          <div className="flex items-center gap-1 px-2 py-1 bg-emerald-100 rounded-full">
            <CheckCircle className="w-4 h-4 text-emerald-600" />
            <span className="text-xs font-medium text-emerald-700">Captured</span>
          </div>
        )}
      </div>

      {location ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-xs text-slate-500 mb-1">Latitude</p>
              <p className="text-sm font-medium text-slate-900">{formatCoordinate(location.lat, 'lat')}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3">
              <p className="text-xs text-slate-500 mb-1">Longitude</p>
              <p className="text-sm font-medium text-slate-900">{formatCoordinate(location.lng, 'lng')}</p>
            </div>
          </div>
          
          <div className="flex items-center justify-between text-xs text-slate-500 px-1">
            <span>Accuracy: ±{Math.round(location.accuracy)}m</span>
            <button
              type="button"
              onClick={captureLocation}
              disabled={loading}
              className="text-sky-600 hover:text-sky-700 font-medium"
              data-testid="refresh-location-btn"
            >
              Refresh
            </button>
          </div>

          {/* Mini map visualization */}
          <div className="relative h-32 bg-gradient-to-br from-sky-100 to-emerald-100 rounded-xl overflow-hidden">
            <div className="absolute inset-0 opacity-30">
              <svg className="w-full h-full" viewBox="0 0 100 100">
                {/* Grid lines */}
                {[...Array(10)].map((_, i) => (
                  <React.Fragment key={i}>
                    <line x1={i * 10} y1="0" x2={i * 10} y2="100" stroke="#0284c7" strokeWidth="0.5" />
                    <line x1="0" y1={i * 10} x2="100" y2={i * 10} stroke="#0284c7" strokeWidth="0.5" />
                  </React.Fragment>
                ))}
              </svg>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative">
                <div className="w-4 h-4 bg-sky-600 rounded-full gps-pulse" />
                <div className="absolute -inset-2 bg-sky-400/30 rounded-full animate-ping" />
              </div>
            </div>
            <div className="absolute bottom-2 left-2 right-2 text-center">
              <p className="text-xs font-medium text-sky-800 bg-white/80 backdrop-blur-sm rounded-lg py-1 px-2">
                Location captured successfully
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div>
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 rounded-xl mb-4">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          
          <button
            type="button"
            onClick={captureLocation}
            disabled={loading}
            data-testid="capture-location-btn"
            className={cn(
              "w-full h-12 rounded-xl font-medium transition-all flex items-center justify-center gap-2",
              "bg-sky-600 text-white hover:bg-sky-700 active:scale-98",
              loading && "opacity-70 cursor-not-allowed"
            )}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Capturing Location...</span>
              </>
            ) : (
              <>
                <Navigation className="w-5 h-5" />
                <span>Capture GPS Location</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default GeoLocationCapture;
